import i18next, { type TFunction } from 'i18next';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type Language, languages } from 'src/core/config/i18n';

import translationsData from './verificationOfPayeeTranslations.json';

// NOTE: This copy is stored in the codebase instead of Lokalise because it is
// compliance-critical messaging. Any changes to these texts require alignment
// with compliance and should go through a proper review process, as opposed to
// Lokalise which could be changed by anyone with write access without review.

type TranslationsStructure = (typeof translationsData)['en'];

const NAMESPACE = 'verificationOfPayee';

/**
 * Create a dedicated i18n instance for verification of payee translations.
 * This keeps compliance-critical translations separate from the main app translations.
 */
const verificationOfPayeeI18n = i18next.createInstance();

verificationOfPayeeI18n.init({
  returnNull: false,
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: [...languages],
  ns: [NAMESPACE],
  defaultNS: NAMESPACE,
  resources: Object.fromEntries(
    languages.map((lang) => [
      lang,
      { [NAMESPACE]: translationsData[lang] ?? {} },
    ]),
  ),
  interpolation: {
    escapeValue: false,
  },
});

/**
 * Hook to access verification of payee translations.
 *
 * @example
 * const { t } = useVerificationOfPayeeTranslations();
 * const title = t('inconsistencyModal.title');
 * const invoices = t('inconsistencyModal.invoice.count', { count: 5 });
 */
export const useVerificationOfPayeeTranslations = (): { t: TFunction } => {
  const { i18n } = useTranslation();
  const activeLanguage = (i18n.resolvedLanguage as Language) || 'en';

  // Sync the verification of payee i18n instance language with the main app
  if (verificationOfPayeeI18n.language !== activeLanguage) {
    verificationOfPayeeI18n.changeLanguage(activeLanguage);
  }

  return { t: verificationOfPayeeI18n.t };
};

export type { TranslationsStructure };
